import React, { useEffect, useRef } from "react";
import { useScreenDimensions, useDevice } from "../_layout";

import { useState } from "react";
import { Text, View, TouchableOpacity } from "react-native";
import * as Sentry from "@sentry/react-native";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { useTranslation } from "react-i18next";
import { Feather, Ionicons, Ionicons as IoniconsLib } from "@expo/vector-icons";

import { observer, Observer } from "mobx-react";

import VideoComponent from "../Components/VideoComponent";
import ImageComponent from "../Components/ImageComponent";
import ChatManager from "../Classes/ChatManager";
import MediaManager from "../Classes/MediaManager";
import { Chat } from "../../Resources/Constants";
import IconEmptyView from "../Components/IconEmptyView";
import LoadingLogo from "../Components/LoadingLogo";
import { getSlotColorWithOpacity } from "../utils/colorUtils";

const ChatMediaPlayer = observer(({ coach, fullScreen = false }) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const { screenSize } = useScreenDimensions();

  const chatManager = ChatManager.getInstance();
  const mediaManager = MediaManager.getInstance();

  const message = mediaManager.messageToView;
  const serie = mediaManager.serieToView;
  const showingVideo = serie || message?.type === "video";
  const currentSerie = message?.getSerie || serie;
  const clientFromMessage = message?.client;
  const clientFromSerie = currentSerie?.exercise?.workout?.block?.client;
  const selectedChatClient = coach?.selectedChatClient;
  const resolvedClient = clientFromMessage || clientFromSerie || selectedChatClient;
  const resolvedCoachId = resolvedClient?.coach?.id || coach?.id || null;
  const resolvedClientId =
    resolvedClient?.id || coach?.selectedChatId || message?.client?.id || null;
  const resolvedSerieId = serie?.id || message?.serieId || currentSerie?.id || null;

  const [url, setUrl] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadError, setDownloadError] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);
  const playbackErrorDedupRef = useRef({ signature: "", time: 0 });

  useEffect(() => {
    let isMounted = true;
    setUrl(false);
    setIsLoading(true);
    setDownloadError(false);
    setPlaybackError(false);
    playbackErrorDedupRef.current = { signature: "", time: 0 };
    const runEffect = async () => {
      if (isMounted) setIsLoading(true);
      try {
        const newUrl = await (message || serie).getAttachmentURL();
        if (newUrl && isMounted) {
          setUrl(newUrl);
        }
      } catch (error) {
        if (isMounted) setDownloadError(true);
        console.error("Error checking if video is downloaded:", error);
      }
      if (isMounted) setIsLoading(false);
    };

    runEffect();
    return () => {
      isMounted = false;
    };
  }, [serie, message]);

  const handlePlaybackError = (payload) => {
    if (!payload?.isCodecDecodeIssue) return;
    setPlaybackError(true);

    const dedupeWindowMs = 5000;
    const signature = [
      message?.id || `serie:${serie?.id || "unknown"}`,
      payload.origin || "unknown",
      payload.mediaErrorCode ?? "na",
      payload.currentSrc || payload.url || url || "na",
      (payload.mediaErrorMessage || payload.message || "")
        .toLowerCase()
        .slice(0, 220),
    ].join("|");

    const now = Date.now();
    const previous = playbackErrorDedupRef.current;
    if (
      previous.signature === signature &&
      now - previous.time < dedupeWindowMs
    ) {
      return;
    }
    playbackErrorDedupRef.current = { signature, time: now };

    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : null;
    const browserPlatform =
      typeof navigator !== "undefined"
        ? navigator.userAgentData?.platform || navigator.platform || null
        : null;

    console.error("[ChatMediaPlayer][VideoPlaybackError]", {
      ...payload,
      messageId: message?.id ?? null,
      serieId: serie?.id ?? null,
      fullScreen,
      isMobile,
      userAgent,
      browserPlatform,
    });

    const tryExtractIdsFromUrl = (value) => {
      if (!value || typeof value !== "string") {
        return { coachId: null, userId: null, serieId: null };
      }

      try {
        const decodedValue = decodeURIComponent(value);

        // Firebase Storage download URLs often encode full storage path:
        // .../o/coaches%2F{coachId}%2Fchats%2F{userId}%2Fvideo_messages%2F{serieId}.mp4
        const encodedMatch = value.match(
          /coaches%2F([^%/]+)%2Fchats%2F([^%/]+)%2Fvideo_messages%2F([^%/.?&]+)\.mp4/i
        );
        if (encodedMatch) {
          return {
            coachId: encodedMatch[1] || null,
            userId: encodedMatch[2] || null,
            serieId: encodedMatch[3] || null,
          };
        }

        // Fallback for already-decoded paths
        const decodedMatch = decodedValue.match(
          /coaches\/([^/]+)\/chats\/([^/]+)\/video_messages\/([^/.?&]+)\.mp4/i
        );
        if (decodedMatch) {
          return {
            coachId: decodedMatch[1] || null,
            userId: decodedMatch[2] || null,
            serieId: decodedMatch[3] || null,
          };
        }
      } catch (_error) {
        // no-op
      }

      return { coachId: null, userId: null, serieId: null };
    };

    const extractedFromCurrentSrc = tryExtractIdsFromUrl(payload.currentSrc);
    const extractedFromUrl = tryExtractIdsFromUrl(payload.url);
    const sentryCoachId =
      resolvedCoachId ||
      extractedFromCurrentSrc.coachId ||
      extractedFromUrl.coachId ||
      "unknown";
    const sentryUserId =
      resolvedClientId ||
      extractedFromCurrentSrc.userId ||
      extractedFromUrl.userId ||
      "unknown";
    const sentrySerieId =
      resolvedSerieId ||
      extractedFromCurrentSrc.serieId ||
      extractedFromUrl.serieId ||
      "unknown";

    try {
      Sentry.withScope((scope) => {
        scope.setTag("component", "ChatMediaPlayer");
        scope.setTag("chat_media_playback_issue", "codec_decode");
        scope.setTag("media_type", message?.type || (serie ? "serie" : "unknown"));
        scope.setTag("playback_origin", payload.origin || "unknown");
        scope.setTag("playback_fullscreen", String(Boolean(payload.isFullScreen)));
        scope.setTag("chat_media_fullscreen", String(Boolean(fullScreen)));
        scope.setTag("platform_mobile", String(Boolean(isMobile)));
        scope.setTag("coachId", String(sentryCoachId));
        scope.setTag("userId", String(sentryUserId));
        scope.setTag("serieId", String(sentrySerieId));
        // Keep snake_case tags for backward compatibility with existing Sentry searches.
        scope.setTag("coach_id", String(sentryCoachId));
        scope.setTag("client_id", String(sentryUserId));
        scope.setTag("serie_id", String(sentrySerieId));

        if (message?.id != null) {
          scope.setExtra("messageId", String(message.id));
        }
        scope.setExtra("coachId", String(sentryCoachId));
        scope.setExtra("userId", String(sentryUserId));
        scope.setExtra("clientId", String(sentryUserId));
        scope.setExtra("serieId", String(sentrySerieId));
        scope.setContext("chatMediaIdentifiers", {
          coachId: String(sentryCoachId),
          userId: String(sentryUserId),
          serieId: String(sentrySerieId),
        });
        if (currentSerie?.exercise?.id != null) {
          scope.setExtra("exerciseId", String(currentSerie.exercise.id));
        }
        if (currentSerie?.exercise?.workout?.id != null) {
          scope.setExtra("workoutId", String(currentSerie.exercise.workout.id));
        }

        scope.setExtra("videoSource", payload.currentSrc || payload.url || null);
        scope.setExtra("mediaErrorCode", payload.mediaErrorCode);
        scope.setExtra("mediaErrorMessage", payload.mediaErrorMessage);
        scope.setExtra("playbackMessage", payload.message);
        scope.setExtra("readyState", payload.readyState);
        scope.setExtra("networkState", payload.networkState);
        scope.setExtra("errorName", payload.errorName);
        scope.setExtra("userAgent", userAgent);
        scope.setExtra("browserPlatform", browserPlatform);

        Sentry.captureException(new Error(payload.message || "Video playback error"));
      });
    } catch (_error) {
      // no-op
    }
  };

  if (downloadError && showingVideo) {
    return (
      <View
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 16,
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
          backgroundColor: Colors.lightGrayColor,
          marginBottom: 8,
        }}
      >
        <IconEmptyView
          iconLibrary={"Ionicons"}
          iconName={"warning-outline"}
          mainText={"videoNotAvailableTitle-string"}
          subText={"videoNotAvailableText-string"}
          marginTop={48}
          marginBottom={24}
        />
      </View>
    );
  }

  if (playbackError && showingVideo) {
    return (
      <View
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 16,
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
          backgroundColor: Colors.lightGrayColor,
          marginBottom: 8,
        }}
      >
        <IconEmptyView
          iconLibrary={"Ionicons"}
          iconName={"warning-outline"}
          mainText={"NoFileFoundTitle-string"}
          subText={"NoFileFoundText-string"}
          marginTop={48}
          marginBottom={24}
        />
      </View>
    );
  }

  return (
    <View
      style={{
        position: "relative",
        // width: "100%",
        borderRadius: 16,
        borderWidth: 0.1,
        borderColor: Colors.borderColor,
        backgroundColor: Colors.lightGrayColor,
        overflow: "hidden",
        marginBottom: 8,
        justifyContent: "flex-end",
        flexShrink: 1,
        // maxHeight: 200,
      }}
    >
      {isLoading ? (
        <LoadingLogo
          container={false}
          width={75}
          height={75}
          flex={true}
          dark={true}
          marginBottom={48}
          marginTop={48}
        />
      ) : showingVideo && url ? (
        <>
          <VideoComponent
            url={url}
            width={
              fullScreen
                ? "auto"
                : screenSize != "small"
                ? coach.showingChatList
                  ? Chat.CHAT_WIDTH - 160
                  : Chat.CHAT_WIDTH
                : coach.showingChatList
                ? Chat.CHAT_WIDTH - 160
                : Chat.CHAT_WIDTH - 90
            }
            nativeControls={false}
            contentFit="cover"
            allowsFullscreen={false}
            allowsPictureInPicture={false}
            loop={true}
            muted={true}
            autoPlay={true}
            onPlaybackError={handlePlaybackError}
            // Overlay props
            weekText={
              currentSerie &&
              currentSerie.exercise?.workout?.week > 0 &&
              currentSerie.exercise?.workout?.block?.totalWeeks > 0
                ? t("clientWeekStatus-string", {
                    blockCurrentWeek: currentSerie.exercise.workout.week,
                    blockWeeks: currentSerie.exercise.workout.block.totalWeeks,
                  })
                : null
            }
            serieDetails={
              currentSerie ? (
                <>
                  {(() => {
                    const parts = currentSerie.getFormattedTextParts(
                      coach.rirOrRpe,
                      coach.exerciseUnit,
                      false
                    );
                    return (
                      <>
                        <Text
                          style={{
                            fontSize: 11,
                            color: Colors.whiteColor,
                          }}
                        >
                          {parts.weightText}
                        </Text>
                        <Text
                          style={{
                            fontSize: 11,
                            color: Colors.whiteColor,
                          }}
                        >
                          {parts.repsText}
                        </Text>
                        <Text
                          style={{
                            fontSize: 11,
                            color: parts.rpeColor,
                          }}
                        >
                          {parts.rpeLabel}
                        </Text>
                        <Text
                          style={{
                            fontSize: 11,
                            color: parts.rpeColor,
                          }}
                        >
                          {parts.rpeValue}
                        </Text>
                      </>
                    );
                  })()}
                </>
              ) : null
            }
            exerciseName={
              currentSerie
                ? currentSerie.exercise.getName(t, i18n.language)
                : null
            }
            protocolSlot={
              currentSerie?.exercise?.protocolSlot
                ? t(currentSerie.exercise.protocolSlot)
                : null
            }
            protocolSlotColor={
              currentSerie?.exercise?.protocolSlot
                ? getSlotColorWithOpacity(currentSerie.exercise.protocolSlot)
                : null
            }
          />
        </>
      ) : message?.type === "image" && url ? (
        <ImageComponent
          imageURL={url}
          width={
            fullScreen
              ? "auto"
              : screenSize != "small"
              ? coach.showingChatList
                ? Chat.CHAT_WIDTH - 120
                : Chat.CHAT_WIDTH
              : coach.showingChatList
              ? Chat.CHAT_WIDTH - 120
              : Chat.CHAT_WIDTH - 90
          }
          maxHeight={"100%"}
          borderRadius={12}
          large={true}
        />
      ) : (
        <IconEmptyView
          iconLibrary={"Ionicons"}
          iconName={"warning-outline"}
          mainText={"NoFileFoundTitle-string"}
          subText={"NoFileFoundText-string"}
          marginTop={48}
          marginBottom={24}
        />
      )}
      <TouchableOpacity
        onPress={() => {
          mediaManager.closeMediaPlayer();
        }}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          borderRadius: 13,
          width: 26,
          height: 26,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Feather name="x" size={20} color={Colors.whiteColor} />
      </TouchableOpacity>
    </View>
  );
});

export default ChatMediaPlayer;
